import { RACK_WIDTH, RACK_HEIGHT } from "./geometry"

const FIT_MARGIN = 20
const DRAG_THRESHOLD = 3
const ZOOM_ANIMATION_DURATION = 200
const ZOOM_STATE_CLUSTER = "cluster"
const ZOOM_STATE_RACK = "rack"

export class PanZoom {
  constructor(svg, scene, wrapper) {
    this.svg = svg
    this.scene = scene
    this.wrapper = wrapper

    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this.zoomState = ZOOM_STATE_CLUSTER
    this.transformAnimator = new TransformAnimator(
      ZOOM_ANIMATION_DURATION,
      this.currentTransform.bind(this),
      this.setTransform.bind(this)
    )

    this.isPanning = false
    this.suppressClick = false
    this.onCursorUpdate = null
    this.pointerStartX = 0
    this.pointerStartY = 0
    this.lastPointerX = 0
    this.lastPointerY = 0
    this.animationClickCleanupTimeout = null

    this.onWheel = this.onWheel.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    this.onAnimationPointerEnd = this.onAnimationPointerEnd.bind(this)
    this.onResize = this.onResize.bind(this)

    this.svg.addEventListener("wheel", this.onWheel, { passive: false })
    this.svg.addEventListener("pointerdown", this.onPointerDown)
  }

  fitToView() {
    const wrapperRect = this.wrapper.getBoundingClientRect()
    const wrapperWidth = wrapperRect.width
    const wrapperHeight = wrapperRect.height

    const bbox = this.scene.getBBox()
    if (bbox.width === 0 || bbox.height === 0) return

    const scale = this.fitScale(bbox.width, bbox.height, wrapperWidth, wrapperHeight)
    if (!scale) return

    this.scale = scale
    this.zoomState = ZOOM_STATE_CLUSTER

    this.translateX = (wrapperWidth - bbox.width * this.scale) / 2 - bbox.x * this.scale
    this.translateY = (wrapperHeight - bbox.height * this.scale) / 2 - bbox.y * this.scale

    this.applyTransform()
    this.updateViewBox(wrapperWidth, wrapperHeight)
  }

  fitScale(width, height, wrapperWidth, wrapperHeight) {
    if (width <= 0 || height <= 0) return null

    const availableWidth = wrapperWidth - FIT_MARGIN * 2
    const availableHeight = wrapperHeight - FIT_MARGIN * 2
    if (availableWidth <= 0 || availableHeight <= 0) return null

    const scaleX = availableWidth / width
    const scaleY = availableHeight / height
    return Math.min(scaleX, scaleY)
  }

  rackScale() {
    const wrapperRect = this.wrapper.getBoundingClientRect()
    return this.fitScale(RACK_WIDTH, RACK_HEIGHT, wrapperRect.width, wrapperRect.height)
  }

  onWheel(event) {
    event.preventDefault()

    let delta = -event.deltaY
    if (event.deltaMode === 1) {
      delta = delta * 20
    }

    const targetZoomState = delta > 0 ? ZOOM_STATE_RACK : ZOOM_STATE_CLUSTER
    if (targetZoomState === this.zoomState) return

    const ctm = this.svg.getScreenCTM()
    if (!ctm) return

    const svgPoint = this.svg.createSVGPoint()
    svgPoint.x = event.clientX
    svgPoint.y = event.clientY
    const pointerSVG = svgPoint.matrixTransform(ctm.inverse())

    const oldScale = this.scale
    let newScale

    if (targetZoomState === ZOOM_STATE_RACK) {
      newScale = this.rackScale()
    } else {
      const bbox = this.scene.getBBox()
      const wrapperRect = this.wrapper.getBoundingClientRect()
      newScale = this.fitScale(bbox.width, bbox.height, wrapperRect.width, wrapperRect.height)
    }

    if (!newScale || oldScale === 0) return

    const ratio = newScale / oldScale
    const targetTransform = this.clampTransform(
      newScale,
      pointerSVG.x * (1 - ratio) + this.translateX * ratio,
      pointerSVG.y * (1 - ratio) + this.translateY * ratio
    )

    this.animateToTransform(targetTransform, targetZoomState)
  }

  onPointerDown(event) {
    if (event.button !== 0) return

    if (this.transformAnimator.isAnimating()) {
      // Ignore clicks whilst animating and jump through hoops to ensure that
      // the next click is animating is not ignored.
      this.clearAnimationPointerCleanup()
      this.suppressClick = true
      this.wrapper.addEventListener("pointerup", this.onAnimationPointerEnd)
      this.wrapper.addEventListener("pointerleave", this.onAnimationPointerEnd)
      return
    }

    this.removeAnimationPointerEndListeners()
    this.isPanning = false
    this.suppressClick = false
    this.pointerStartX = event.clientX
    this.pointerStartY = event.clientY
    this.lastPointerX = event.clientX
    this.lastPointerY = event.clientY

    this.wrapper.addEventListener("pointermove", this.onPointerMove)
    this.wrapper.addEventListener("pointerup", this.onPointerUp)
    this.wrapper.addEventListener("pointerleave", this.onPointerUp)
  }

  onPointerMove(event) {
    const dx = event.clientX - this.lastPointerX
    const dy = event.clientY - this.lastPointerY

    if (!this.isPanning) {
      const totalDx = event.clientX - this.pointerStartX
      const totalDy = event.clientY - this.pointerStartY
      if (Math.sqrt(totalDx * totalDx + totalDy * totalDy) > DRAG_THRESHOLD) {
        this.isPanning = true
        if (this.onCursorUpdate) this.onCursorUpdate()
      }
    }

    if (this.isPanning) {
      this.translateX += dx
      this.translateY += dy
      this.clamp()
      this.applyTransform()
    }

    this.lastPointerX = event.clientX
    this.lastPointerY = event.clientY
  }

  onPointerUp(event) {
    this.wrapper.removeEventListener("pointermove", this.onPointerMove)
    this.wrapper.removeEventListener("pointerup", this.onPointerUp)
    this.wrapper.removeEventListener("pointerleave", this.onPointerUp)

    if (this.isPanning) {
      this.suppressClick = true
    }

    this.isPanning = false
    if (this.onCursorUpdate) this.onCursorUpdate()
  }

  onAnimationPointerEnd(event) {
    this.removeAnimationPointerEndListeners()

    this.clearAnimationPointerCleanup()
    // Keep suppression through the `click` event generated after this
    // `pointerup` event, then clear it for ignored animation interactions that
    // never dispatch a click.
    this.animationClickCleanupTimeout = setTimeout(() => {
      this.suppressClick = false
      this.animationClickCleanupTimeout = null
    }, 0)
  }

  clearAnimationPointerCleanup() {
    if (!this.animationClickCleanupTimeout) return

    clearTimeout(this.animationClickCleanupTimeout)
    this.animationClickCleanupTimeout = null
  }

  removeAnimationPointerEndListeners() {
    this.wrapper.removeEventListener("pointerup", this.onAnimationPointerEnd)
    this.wrapper.removeEventListener("pointerleave", this.onAnimationPointerEnd)
  }

  clamp() {
    const bbox = this.scene.getBBox()
    if (bbox.width === 0 || bbox.height === 0) return

    const transform = this.clampTransform(this.scale, this.translateX, this.translateY)
    this.scale = transform.scale
    this.translateX = transform.translateX
    this.translateY = transform.translateY
  }

  clampTransform(scale, translateX, translateY) {
    const bbox = this.scene.getBBox()
    if (bbox.width === 0 || bbox.height === 0) {
      return { scale: scale, translateX: translateX, translateY: translateY }
    }

    const wrapperRect = this.wrapper.getBoundingClientRect()
    const wrapperWidth = wrapperRect.width
    const wrapperHeight = wrapperRect.height

    const contentWidth = bbox.width * scale
    const contentHeight = bbox.height * scale

    let minTranslateX
    let maxTranslateX
    let minTranslateY
    let maxTranslateY

    if (contentWidth > wrapperWidth) {
      minTranslateX = wrapperWidth - (bbox.x + bbox.width) * scale
      maxTranslateX = -bbox.x * scale
      if (translateX < minTranslateX) { translateX = minTranslateX }
      if (translateX > maxTranslateX) { translateX = maxTranslateX }
    } else {
      translateX = (wrapperWidth - contentWidth) / 2 - bbox.x * scale
    }

    if (contentHeight > wrapperHeight) {
      minTranslateY = wrapperHeight - (bbox.y + bbox.height) * scale
      maxTranslateY = -bbox.y * scale
      if (translateY < minTranslateY) { translateY = minTranslateY }
      if (translateY > maxTranslateY) { translateY = maxTranslateY }
    } else {
      translateY = (wrapperHeight - contentHeight) / 2 - bbox.y * scale
    }

    return { scale: scale, translateX: translateX, translateY: translateY }
  }

  animateToTransform(targetTransform, targetZoomState) {
    this.zoomState = targetZoomState
    this.transformAnimator.animateTo(targetTransform)
  }

  currentTransform() {
    return {
      scale: this.scale,
      translateX: this.translateX,
      translateY: this.translateY
    }
  }

  setTransform(transform) {
    this.scale = transform.scale
    this.translateX = transform.translateX
    this.translateY = transform.translateY
    this.applyTransform()
  }

  applyTransform() {
    this.scene.setAttribute("transform",
      "translate(" + this.translateX + "," + this.translateY + ") " +
      "scale(" + this.scale + ")"
    )
  }

  onResize(event) {
    // Cancel any in-progress zoom animation, so a stale animation frame cannot
    // overwrite the transform, we're about to recalculate from the new wrapper
    // size.
    this.transformAnimator.cancel()

    if (this.zoomState === ZOOM_STATE_CLUSTER) {
      this.fitToView()
      return
    }

    const wrapperRect = this.wrapper.getBoundingClientRect()
    const oldScale = this.scale
    const newScale = this.fitScale(RACK_WIDTH, RACK_HEIGHT, wrapperRect.width, wrapperRect.height)

    if (!newScale || oldScale === 0) return

    const ratio = newScale / oldScale
    const targetTransform = this.clampTransform(
      newScale,
      this.translateX * ratio,
      this.translateY * ratio,
    )
    this.setTransform(targetTransform)
    this.updateViewBox(wrapperRect.width, wrapperRect.height)
  }

  updateViewBox(width, height) {
    this.svg.setAttribute("viewBox", "0 0 " + width + " " + height)
    this.svg.setAttribute("width", width)
    this.svg.setAttribute("height", height)
  }

  destroy() {
    this.transformAnimator.cancel()
    this.clearAnimationPointerCleanup()
    this.svg.removeEventListener("wheel", this.onWheel)
    this.svg.removeEventListener("pointerdown", this.onPointerDown)
    this.wrapper.removeEventListener("pointermove", this.onPointerMove)
    this.wrapper.removeEventListener("pointerup", this.onPointerUp)
    this.wrapper.removeEventListener("pointerleave", this.onPointerUp)
    this.removeAnimationPointerEndListeners()
  }
}

class TransformAnimator {
  constructor(duration, getTransform, setTransform) {
    this.duration = duration
    this.getTransform = getTransform
    this.setTransform = setTransform
    this.animation = null
    this.animationFrame = null

    this.onAnimationFrame = this.onAnimationFrame.bind(this)
  }

  isAnimating() {
    return !!this.animation
  }

  animateTo(targetTransform) {
    this.cancel()

    const startTransform = this.getTransform()
    this.animation = {
      startTime: null,
      startScale: startTransform.scale,
      startTranslateX: startTransform.translateX,
      startTranslateY: startTransform.translateY,
      targetScale: targetTransform.scale,
      targetTranslateX: targetTransform.translateX,
      targetTranslateY: targetTransform.translateY
    }
    this.animationFrame = requestAnimationFrame(this.onAnimationFrame)
  }

  onAnimationFrame(timestamp) {
    if (!this.animation) return

    if (this.animation.startTime === null) {
      this.animation.startTime = timestamp
    }

    const elapsed = timestamp - this.animation.startTime
    const progress = Math.min(elapsed / this.duration, 1)
    const easedProgress = 1 - Math.pow(1 - progress, 3)

    if (progress < 1) {
      this.setTransform({
        scale: this.interpolate(this.animation.startScale, this.animation.targetScale, easedProgress),
        translateX: this.interpolate(this.animation.startTranslateX, this.animation.targetTranslateX, easedProgress),
        translateY: this.interpolate(this.animation.startTranslateY, this.animation.targetTranslateY, easedProgress)
      })
      this.animationFrame = requestAnimationFrame(this.onAnimationFrame)
      return
    }

    this.setTransform({
      scale: this.animation.targetScale,
      translateX: this.animation.targetTranslateX,
      translateY: this.animation.targetTranslateY
    })
    this.animation = null
    this.animationFrame = null
  }

  interpolate(start, target, progress) {
    return start + (target - start) * progress
  }

  cancel() {
    const wasAnimating = this.isAnimating()
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
      this.animationFrame = null
    }
    this.animation = null
    return wasAnimating
  }
}
